import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import useCapabilities, { PERMISSION } from "@/hooks/useCapabilities";
import { usePrimarySection } from "@/hooks/usePrimarySection";
import { GET_MANAGEABLE_SECTIONS } from "@/pages/events/queries";
import type { Section } from "@/types";
import { HQ_WIDE_ROLES, NATIONAL_SECTION_LEGACY_ID, NATIONAL_SECTION_NAME } from "../constants";

type ManageableSectionsQueryData = {
  manageableSections: {
    nodes: Section[];
  };
};

export function useDashboardEventSectionIds(): {
  sectionIds: string[];
  scopedSectionNames: string[];
  loading: boolean;
  ready: boolean;
} {
  const { hasRole, hasPermission, loaded: capabilitiesLoaded } = useCapabilities();
  const canManageEvents = capabilitiesLoaded && hasPermission(PERMISSION.manageEvents);
  const { primarySectionId, loading: primarySectionLoading } = usePrimarySection();

  const { data: sectionsData, loading: sectionsLoading } = useQuery<ManageableSectionsQueryData>(
    GET_MANAGEABLE_SECTIONS,
    {
      skip: !capabilitiesLoaded || !canManageEvents,
      variables: { input: { search: null } }
    }
  );

  const manageableSectionNodes = sectionsData?.manageableSections?.nodes;
  const hasHqWideAccess = HQ_WIDE_ROLES.some((role) => hasRole(role));

  const nationalSectionId = useMemo(() => {
    return (
      manageableSectionNodes?.find(
        (section) => section.legacyId === NATIONAL_SECTION_LEGACY_ID || section.name === NATIONAL_SECTION_NAME
      )?.id ?? null
    );
  }, [manageableSectionNodes]);

  const sectionIds = useMemo(() => {
    if (!canManageEvents) {
      return [];
    }

    const ids: string[] = [];

    if (primarySectionId) {
      ids.push(primarySectionId);
    } else if (!hasHqWideAccess && manageableSectionNodes?.length) {
      ids.push(...manageableSectionNodes.map((section) => section.id));
    }

    if (hasHqWideAccess && nationalSectionId) {
      ids.push(nationalSectionId);
    }

    return [...new Set(ids)];
  }, [canManageEvents, hasHqWideAccess, manageableSectionNodes, nationalSectionId, primarySectionId]);

  const scopedSectionNames = useMemo(() => {
    if (sectionIds.length === 0) {
      return [];
    }

    const nameById = new Map((manageableSectionNodes ?? []).map((section) => [section.id, section.name]));
    const names = sectionIds.map((id) => nameById.get(id));

    if (names.every((name) => Boolean(name))) {
      return names.filter((name): name is string => Boolean(name));
    }

    // The primary section can come from usePrimarySection's all-sections query while
    // manageableSectionNodes only contains sections the user can manage. Avoid showing
    // a partial list when any scoped section id cannot be resolved to a name.
    return [
      sectionIds.length === 1 ? "your associated section" : `${sectionIds.length} sections you're associated with`
    ];
  }, [manageableSectionNodes, sectionIds]);

  const loading = !capabilitiesLoaded || (canManageEvents && (sectionsLoading || primarySectionLoading));
  const ready = capabilitiesLoaded && (!canManageEvents || (!sectionsLoading && !primarySectionLoading));

  return {
    sectionIds,
    scopedSectionNames,
    loading,
    ready
  };
}
